import React, { useEffect, useState } from "react";
import Input from "../../FormInputs";
import { useFormik } from "formik";
import {
  franchiseOfferSchema,
  uploadOfferSchema,
} from "../../../../utils/validationSchemas";
import {
  inferCountryFromPhoneNumber,
  normalizePhoneNumberForInput,
} from "../../../../utils/phoneValidation";
import PrimaryButton from "../../PrimaryButton";
import DayTimeSelector from "./DayTimeSelector";
import GoogleMaps from "../../../common/GoogleMaps";
import UploadExcel from "./UploadExcel";
import {
  addFranchise,
  getLocationById,
  editFranchise,
  getFranchises,
} from "../../../../services/franchiseService";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PhoneFormInput from "../../PhoneInput";

dayjs.extend(customParseFormat);

const AddNewLocation = ({
  allStores,
  setLocations,
  setSelectedLocations,
  setSelectedLocationIds,
  setLocationNames,
  selectedMerchant,
  onSuccess,
  isDrawer = false,
  editData = null
}) => {
  const dispatch = useDispatch();
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );

  const merchantId = React.useMemo(() => {
    if (!selectedMerchant) return null;
    return typeof selectedMerchant === 'string' ? selectedMerchant : selectedMerchant?._id;
  }, [selectedMerchant]);
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const format = "hh:mm A";

  const [fileData, setFileData] = useState();
  const [placeId, setPlaceId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [newLocationId, setNewLocationId] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchedEditData, setFetchedEditData] = useState(null);

  const initialValues = {
    franchiseName: "",
    email: "",
    contact: "",
    contactCountry: "AU",
    location: "",
    timeSet: false,
    timeData: days?.map((d) => ({
      day: d,
      openTime: undefined,
      closeTime: undefined,
      isSelected: false,
    })),
  };

  // Exhaustively find any form of ID
  const getId = (data) => {
    if (!data) return null;
    if (typeof data === 'string' && /^[0-9a-fA-F]{24}$/.test(data)) return data;
    const foundId = data?._id || data?.id || data?.storeId || data?.StoreId || data?.pk || data?.pk_id || data?.uid || data?.uuid || data?.key;
    if (!foundId && typeof data === 'object') {
      console.warn("[AddNewLocation] No ID found in object. Available keys:", Object.keys(data));
    }
    return foundId;
  };

  const editId = getId(editData) || getId(fetchedEditData);

  useEffect(() => {
    console.log("[AddNewLocation] Logging editData keys:", editData ? Object.keys(editData) : "null");
    console.log("[AddNewLocation] Raw editData:", editData);
    console.log("[AddNewLocation] Detected editId:", editId);
  }, [editData, editId]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: franchiseOfferSchema,

    onSubmit: async (values) => {
      if (allStores) return;
      setIsSubmitting(true);

      const tradingHours = timeData
        .filter((day) => day?.isSelected === true)
        .map((day) => ({
          day: day?.day?.toLowerCase(),
          openTime: day?.openTime ? (typeof day.openTime === 'string' ? day.openTime : day.openTime.format(format)) : null,
          closeTime: day?.closeTime ? (typeof day.closeTime === 'string' ? day.closeTime : day.closeTime.format(format)) : null,
        }));

      const payload =
        role === "admin"
          ? {
            merchant: merchantId,
            name: values?.franchiseName,
            email: values?.email,
            phone: values?.contact,
            phoneCountry: values?.contactCountry,
            googlePlaceId: placeId !== null ? placeId : (editData?.googlePlaceId || null),
            tradingHours: tradingHours,
          }
          : {
            name: values?.franchiseName,
            email: values?.email,
            phone: values?.contact,
            phoneCountry: values?.contactCountry,
            googlePlaceId: placeId !== null ? placeId : (editData?.googlePlaceId || null),
            tradingHours: tradingHours,
          };

      try {
        let response;
        const finalId = editId || getId(values) || getId(editData) || getId(fetchedEditData);
        
        if (finalId) {
          console.log("[AddNewLocation] Performing PATCH with ID:", finalId);
          response = await dispatch(editFranchise({ id: finalId, ...payload })).unwrap();
        } else {
          console.log("[AddNewLocation] Performing POST (Create)");
          response = await dispatch(addFranchise(payload)).unwrap();
        }

        if (response?.success === true) {
          if (!editData) {
            setNewLocationId(response?.data?._id);
            if (setSelectedLocations) setSelectedLocations((prev) => [response?.data, ...prev]);
            if (setSelectedLocationIds) setSelectedLocationIds((prev) => [response?.data?._id, ...prev]);
            if (setLocationNames) setLocationNames((prev) => [response?.data?.name, ...prev]);
          }
          resetForm();
          setPlaceId(null);
          setResetKey(prev => prev + 1);
          if (onSuccess) onSuccess();
        }
      } catch (error) {
        console.error("Error saving location:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const getLocationData = async () => {
    const getPromise = await dispatch(
      getLocationById({ id: newLocationId })
    )?.unwrap();
    if (getPromise?.data) {
      setLocations((prev) => [getPromise?.data, ...prev]);
    }
  };

  useEffect(() => {
    if (newLocationId !== null && !allStores && !editId) {
      getLocationData();
    }
  }, [newLocationId, allStores, editId]);



  useEffect(() => {
    const getFullLocation = async () => {
      let targetId = getId(editData);
      
      // SMART FALLBACK: If no ID found, try to find it in the merchant's full location list
      if (!targetId && editData && merchantId) {
        console.log("[AddNewLocation] ID missing. Attempting Smart Match fallback...");
        try {
          const allLocs = await dispatch(getFranchises({ merchant: merchantId, limit: 100 })).unwrap();
          const match = allLocs?.data?.find(l => 
            (l.phone && l.phone === editData.contact) || 
            (l.address && l.address === editData.location) ||
            (l.name && l.name === editData.name)
          );
          if (match) {
            targetId = match._id || match.id;
            console.log("[AddNewLocation] Smart Match SUCCESS! Found ID:", targetId);
          }
        } catch (err) {
          console.error("[AddNewLocation] Smart Match failed:", err);
        }
      }

      if (targetId) {
        console.log("[AddNewLocation] !!! TRIGGERING DETAIL FETCH for ID:", targetId);
        try {
          const res = await dispatch(getLocationById({ id: targetId })).unwrap();
          console.log("[AddNewLocation] API SUCCESS:", res);

          let storeDetails = res?.data || res?.location || res?.store || (Array.isArray(res) ? res[0] : res);
          setFetchedEditData({ ...editData, ...(storeDetails || {}), _id: targetId });
        } catch (err) {
          console.error("[AddNewLocation] API FAILED:", err);
          setFetchedEditData({ ...editData, _id: targetId });
        }
      } else if (editData) {
        console.log("[AddNewLocation] No ID found even after Smart Match. Props:", editData);
        setFetchedEditData(editData);
      } else {
        setFetchedEditData(null);
      }
    };

    getFullLocation();
  }, [editData, merchantId]);

  const { franchiseName, email, contact, contactCountry, location, timeSet, timeData } =
    formik.values;

  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldError,
    handleSubmit,
    setValues,
    setErrors,
    resetForm,
  } = formik;

  // Populate form AFTER formik is ready, whenever fetchedEditData changes
  useEffect(() => {
    if (!fetchedEditData) {
      // If we are in "Add" mode (no editData), ensure the form is cleared
      if (!editData) {
        resetForm();
        setPlaceId(null);
      }
      return;
    }

    let tHours = fetchedEditData.tradingHours || fetchedEditData.workingHours || fetchedEditData.hours || [];
    if (tHours && !Array.isArray(tHours) && typeof tHours === 'object') {
      tHours = Object.entries(tHours).map(([day, hours]) => ({
        day,
        openTime: hours?.open || hours?.openTime || hours?.startTime,
        closeTime: hours?.close || hours?.closeTime || hours?.endTime,
      }));
    }

    const parseTime = (val) => {
      if (!val) return undefined;
      const fmts = ["hh:mm A", "h:mm A", "HH:mm", "HH:mm:ss", "h:mmA", "hh:mmA"];
      for (const f of fmts) {
        const t = dayjs(val, f); // Remove strict flag to be more forgiving
        if (t.isValid()) return t;
      }
      const iso = dayjs(val);
      if (iso.isValid()) return iso;
      return undefined;
    };

    const formattedTradingHours = days.map(dayName => {
      const found = Array.isArray(tHours) ? tHours.find(th => {
        const d = (th.day || th.name || th.dayName || "").toLowerCase();
        return d === dayName.toLowerCase() || 
               d === dayName.substring(0, 3).toLowerCase() ||
               dayName.toLowerCase().startsWith(d);
      }) : null;

      const openRaw = found?.openTime || found?.open || found?.startTime;
      const closeRaw = found?.closeTime || found?.close || found?.endTime;
      const oTime = parseTime(openRaw);
      const cTime = parseTime(closeRaw);

      return {
        day: dayName,
        isSelected: !!(oTime || cTime),
        openTime: oTime,
        closeTime: cTime,
      };
    });

    const addr =
      fetchedEditData.address ||
      fetchedEditData.location ||
      (typeof fetchedEditData.googlePlaceId === 'string'
        ? fetchedEditData.googlePlaceId
        : fetchedEditData.googlePlaceId?.address) ||
      "";

    const rawPhone = fetchedEditData.phone || fetchedEditData.contact || "";
    const { phone: normalizedPhone, country: inferredCountry } =
      normalizePhoneNumberForInput(rawPhone, "AU");

    setValues({
      franchiseName: fetchedEditData.name || fetchedEditData.franchiseName || "",
      email: fetchedEditData.email || "",
      contact: normalizedPhone,
      contactCountry: inferredCountry,
      location: addr,
      timeSet: formattedTradingHours.some(th => th.isSelected),
      timeData: formattedTradingHours,
    });

    if (addr) {
      const gId = fetchedEditData.googlePlaceId;
      const actualIdentifier = (gId && typeof gId === 'object') 
        ? (gId.googlePlaceId || gId.placeId || gId.place_id || gId.id || addr)
        : (gId || addr);
        
      setPlaceId(actualIdentifier);
    }
  }, [fetchedEditData, editData]);

  const handleTimeChange = (dayIndex, field, time) => {
    const updatedTimeData = [...timeData];
    updatedTimeData[dayIndex][field] = time;
    setFieldValue("timeData", updatedTimeData);
  };

  useEffect(() => {
    const isAnyDaySelected = timeData?.some((day) => day.isSelected === true);
    // timeSet value is true if NO day is selected (invalid)
    // and false if AT LEAST ONE day is selected (valid)
    const nextTimeSet = !isAnyDaySelected;

    if (formik.values.timeSet !== nextTimeSet) {
      setFieldValue("timeSet", nextTimeSet, true);
    }

    // If they deselect all days after having it selected, make sure it's touched
    if (!isAnyDaySelected && touched.timeData) {
      formik.setFieldTouched("timeSet", true, false);
    }
  }, [timeData, formik.values.timeSet, setFieldValue, touched.timeData]);

  const handleLocationSelect = (selectedLocation) => {
    if (!selectedLocation) {
      setFieldValue("location", "", true);
      setPlaceId(null);
      formik.setFieldTouched("location", true);
      return;
    }
    setFieldValue("location", selectedLocation?.placeName, true);
    setPlaceId(selectedLocation?.placeId);
    setShowMap(false);
    formik.setFieldError("location", null);
  };

  const handleLocationClick = () => {
    setShowMap(true);
  };

  const handleCancel = () => {
    formik.resetForm();
    setPlaceId(null);
    setResetKey(prev => prev + 1); // Reset map
    if (onSuccess) onSuccess();
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched so validation errors appear
    formik.setTouched({
      franchiseName: true,
      email: true,
      contact: true,
      location: true,
      timeSet: true,
    });

    const formErrors = await formik.validateForm();

    if (!location || !placeId) {
      formik.setFieldError("location", "Please select a location from the suggestions.");
      return;
    }

    if (Object.keys(formErrors).length === 0) {
      formik.handleSubmit(e);
    }
  };

  return (
    <div className={`w-full ${isDrawer ? 'drawer-content-wrapper' : ''}`}>
      <div className="w-full mt-[-20px]">
        <h3 className="section-title !mb-1">Store Information<span>*</span></h3>
        <Input
          type="text"
          label=""
          placeholder="Enter store name"
          width="100%"
          radius="5px"
          InputDivClass="w-[100%] mb-4 -mt-4"
          name="franchiseName"
          value={franchiseName}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.franchiseName}
          touched={touched.franchiseName}
        />
        <Input
          type="text"
          label=""
          placeholder="Enter email"
          width="100%"
          radius="5px"
          InputDivClass="w-[100%] mb-4"
          name="email"
          value={email}
          handleChange={handleChange}
          handleBlur={handleBlur}
          error={errors.email}
          touched={touched.email}
        />
        <PhoneFormInput
          label={""}
          divClassName="w-full mb-6 relative z-[1005]"
          name="contact"
          country={contactCountry}
          countryFieldName="contactCountry"
          value={contact}
          placeholder="Enter contact number"
          handleBlur={handleBlur}
          error={errors.contact}
          touched={touched.contact}
          setFieldValue={setFieldValue}
          inputStyle={{ width: "100%", border: "none" }}
          buttonStyle={{ border: "none" }}
          buttonClass="!rounded-l-[5px] !bg-[#F0F0F0] md:!w-[13%] sm:!w-[15%] lg:!w-[10%] xl:!w-[12%] 2xl:!w-[10%]"
          containerClass="!rounded-[5px] !shadow-none"
          inputClass="lg:!h-[45px] md:!h-[45px] !rounded-[5px] !bg-[#FFFFFF] !text-[#303030] lg:!text-[14px] sm:!text-[14px] !font-[400] !outline-none !font-poppins !pl-[50px] !shadow-none"
          labelClass="hidden"
          errorClass="text-red-600 text-[10px] md:text-[12px] font-[500] ms-1 mt-1 block text-start"
        />

        <div className="mb-6 rounded-[8px] border border-[#E0E0E0] relative z-[1001]">
          <GoogleMaps
            key={`${resetKey}-${editId || 'new'}`} // Force re-mount to clear internal state on reset
            onLocationSelect={handleLocationSelect}
            onError={(err) => {
              if (err) setFieldError("location", err);
            }}
            searchContainerClass="p-0"
            compactMode={false}
            defaultAddress={location}
            externalSearchValue={location}
            forceOpenSearch={false}
            placeholder="Enter Business Address"
            options={{ mapTypeControl: false, streetViewControl: false }}
            mapContainerStyle="swoodle-map h-[320px] w-full relative"
            searchBarParentClassName="absolute z-[10] top-3 left-3 right-3"
            searchBarDivClassName="w-full flex items-center h-[45px] relative"
            searchBarInputClassName="navSearchBar h-full w-[100%] ps-4 pe-10 text-[14px] font-[400] font-poppins outline-none bg-white text-[#303030] rounded-[5px] border border-[#D9D9D9] placeholder:!text-[#767676] shadow-sm"
            searchIconClassName="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4"
          />
        </div>

        {errors.location && touched.location && (
          <span className="text-red-600 text-[10px] md:text-[12px] font-[500] ms-1 mt-[-20px] mb-6 block text-start font-poppins">
            {errors.location}
          </span>
        )}

        <div className="time-table-card !pt-0">
          <h3 className="section-title -mt-2">Set Your Opening Days & Hours<span>*</span></h3>
          <DayTimeSelector
            timeData={timeData}
            setFieldValue={(name, value, validate) => {
              setFieldValue(name, value, validate);
              if (name.includes("isSelected")) {
                formik.setFieldTouched("timeData", true, false);
              }
            }}
            format={format}
            error={errors?.timeData}
            touched={touched?.timeData}
          />
        </div>

        {errors?.timeSet && touched?.timeSet && (
          <div className="ps-1 mt-2 mb-4">
            <span className="text-red-600 text-[10px] md:text-[12px] font-[500] block text-start font-poppins">
              {errors?.timeSet}
            </span>
          </div>
        )}
      </div>

      {
        isDrawer ? (
          <div className="drawer-footer">
            <span className="cancel-link" onClick={handleCancel}>Cancel</span>
            <button
              type="button"
              className="confirm-btn"
              onClick={handleCustomSubmit}
              disabled={
                isSubmitting ||
                !franchiseName ||
                !email ||
                !contact ||
                !location ||
                !placeId ||
                timeSet
              }
            >
              {isSubmitting 
                ? (editData || fetchedEditData || editId ? "Updating..." : "Adding...") 
                : (editData || fetchedEditData || editId ? "Update" : "Add")}
            </button>
          </div>
        ) : (
          <div className="flex justify-end mt-8">
            <PrimaryButton
              className="w-[140px] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat"
              label="Submit"
              onClick={handleCustomSubmit}
              disabled={
                isSubmitting ||
                !franchiseName ||
                !email ||
                !contact ||
                !location ||
                !placeId ||
                timeSet
              }
            />
          </div>
        )
      }
    </div >
  );
};

export default AddNewLocation;
